import { useState, useCallback, useRef, useEffect } from 'react'
import { extractDeepC2PAMetadata } from './utils/c2paExtractor'
import { calculateTrueValidationStatus, hasValidTimestamp, isSignerTrusted } from './utils/c2paTimestampValidator'
import { detectAiContent } from './utils/aiContentDetector'
import { generateBadgeSvg, svgToDataUri, svgToBlobUrl } from './utils/generateBadge'
import { computeFileHash } from './utils/computeFileHash'
import { generateThumbnail } from './utils/generateThumbnail'
import { createVerification } from './utils/verifiedditApi'
import { validateMediaViaApi } from './utils/verifiedditValidateApi'
import { exportVerificationCertificate } from './utils/exportCertificate'
import { attemptCredentialRecovery, isEcosystemSigner } from './utils/credentialRecovery'
import { ProvenanceDiagram } from './components/ProvenanceDiagram'
import { AiDetectionBanner } from './components/AiDetectionBanner'
import { GPSMap } from './components/GPSMap'
import { analyzeImageForAi, hasAiDetectConsent } from './utils/aiDetectionApi'
import { determineVerdict } from './utils/verificationVerdict'
import type { PixelCheckStatus } from './utils/verificationVerdict'
import type { AiDetectApiResponse } from './utils/aiDetectionApi'
import type { BadgeStatus } from './utils/generateBadge'
import type { ValidationResults } from './types/metadata'

// When served from www.thephenom.app (CF Pages), API calls go to drop.thephenom.app (CORS enabled)
// When served from drop.thephenom.app directly, use relative paths
const API_BASE = (() => {
  if (typeof window !== 'undefined' && window.location.hostname === 'www.thephenom.app') {
    return 'https://drop.thephenom.app'
  }
  return ''
})()

const UPLOAD_API = import.meta.env.VITE_UPLOAD_API_ENDPOINT || `${API_BASE}/api/drop/upload`

const MAX_FILE_SIZE = 2_000_000_000 // 2 GB

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return (bytes / Math.pow(k, i)).toFixed(i >= 2 ? 1 : 0) + ' ' + sizes[i]
}

type VerificationState = 'idle' | 'verifying' | 'verified' | 'uploading' | 'uploaded' | 'error' | 'rejected'

interface VerificationResult {
  hasC2PA: boolean
  isValid: boolean
  isTrusted: boolean
  hasTimestamp: boolean
  issuer?: string
  signedAt?: string
  claimGenerator?: string
  badgeStatus: BadgeStatus
  aiDetection?: { isAiGenerated: boolean; note: string; detectedTools: string[]; digitalSourceType?: string }
  validationResults?: ValidationResults
  formatNote?: string
  pendingServerVerification?: boolean
  gpsCoords?: { lat: number; lon: number }
}

function App() {
  const [file, setFile] = useState<File | null>(null)
  const [state, setState] = useState<VerificationState>('idle')
  const [result, setResult] = useState<VerificationResult | null>(null)
  const [password, setPassword] = useState('')
  const [passcodeSent, setPasscodeSent] = useState(false)
  const [sendingPasscode, setSendingPasscode] = useState(false)
  const [error, setError] = useState('')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [dragOver, setDragOver] = useState(false)
  const [fileHash, setFileHash] = useState<string | null>(null)
  const [shortUrl, setShortUrl] = useState<string | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [manifestStore, setManifestStore] = useState<Record<string, any> | null>(null)
  const [aiAnalyzing, setAiAnalyzing] = useState(false)
  const [aiResult, setAiResult] = useState<AiDetectApiResponse | null>(null)
  const [gpsCoords, setGpsCoords] = useState<{ lat: number; lon: number } | null>(null)
  const [uploaderEmail, setUploaderEmail] = useState('')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [newsletterConsent, _setNewsletterConsent] = useState(false)
  const [isExistingUser, setIsExistingUser] = useState<boolean | null>(null)
  const [rejectionDetails, setRejectionDetails] = useState<{
    reason: string
    confidence: string
    generator: string
    badgeUrl: string
    proofUrl: string
  } | null>(null)
  const [rejectionEmail, setRejectionEmail] = useState('')
  const [rejectionEmailSent, setRejectionEmailSent] = useState(false)
  const [identityHydrated, setIdentityHydrated] = useState(false)
  const [identityName, setIdentityName] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Hydrate identity from URL query params set by /contact.html on the www site.
  // If no valid email is present, this page is being visited directly — redirect
  // back to /contact so the lead is captured first.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const emailParam = (params.get('email') || '').trim()
    const firstNameParam = (params.get('firstname') || '').trim()
    const lastNameParam = (params.get('lastname') || '').trim()
    const userStatusParam = (params.get('user_status') || '').trim()

    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailParam)) {
      setUploaderEmail(emailParam)
      setIdentityName(`${firstNameParam} ${lastNameParam}`.trim())
      if (userStatusParam === 'user') setIsExistingUser(true)
      else if (userStatusParam === 'prospect') setIsExistingUser(false)
      setIdentityHydrated(true)
      return
    }

    // Staging is a QA target — render the form locally so testers can hit it
    // directly without going through the prod marketing site's lead capture.
    // identityHydrated stays false, so the in-page email input UI is shown.
    if (window.location.hostname === 'drop-staging.thephenom.app') return

    // No valid identity — bounce back to contact.
    const target = window.location.hostname === 'www.thephenom.app'
      ? '/contact.html'
      : 'https://www.thephenom.app/contact.html'
    window.location.replace(target)
  }, [])

  const handleFileSelect = useCallback((selectedFile: File) => {
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError(`File too large (${formatFileSize(selectedFile.size)}). Maximum size is ${formatFileSize(MAX_FILE_SIZE)}.`)
      return
    }
    setFile(selectedFile)
    setResult(null)
    setError('')
    setState('idle')
    setUploadProgress(0)
    setFileHash(null)
    setShortUrl(null)
    setManifestStore(null)
    setRejectionDetails(null)
    setRejectionEmail('')
    setRejectionEmailSent(false)
    setGpsCoords(null)
  }, [])

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const dropped = e.dataTransfer.files[0]
    if (dropped) handleFileSelect(dropped)
  }, [handleFileSelect])

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const onDragLeave = useCallback(() => setDragOver(false), [])

  const verify = useCallback(async () => {
    if (!file) return
    setState('verifying')
    setError('')

    try {
      const isImage = file.type.startsWith('image/')
      const isVideo = file.type.startsWith('video/') || /\.(mp4|mov|avi|webm|mkv|m4v|3gp|wmv|flv|mpeg|mpg)$/i.test(file.name)
      const isLargeFile = file.size > 200_000_000 // >200MB
      const hasConsent = hasAiDetectConsent()

      // Determine pixel-check status upfront
      let pixelCheckStatus: PixelCheckStatus = 'completed'
      if (isVideo) pixelCheckStatus = 'skipped'
      else if (!isImage) pixelCheckStatus = 'skipped'
      else if (!hasConsent) pixelCheckStatus = 'declined'

      // Compute file hash first — needed by AI detection and registration
      const hash = await computeFileHash(file)
      setFileHash(hash)

      // Extract GPS metadata for images. exifr.gps() is a focused, fast call
      // that returns {latitude, longitude} or null. Skip WebP (no EXIF parser
      // in exifr v7), video (different metadata format), and unsupported types.
      if (isImage && file.type !== 'image/webp') {
        try {
          const exifr = (await import('exifr')).default
          const gps = await exifr.gps(file).catch(() => null)
          if (gps && typeof gps.latitude === 'number' && typeof gps.longitude === 'number') {
            setGpsCoords({ lat: gps.latitude, lon: gps.longitude })
          }
        } catch {
          // Best-effort: missing GPS is the common case, not an error.
        }
      }

      if (isVideo || isLargeFile) {
        // Large files / video: show pending state, allow upload, verify server-side after
        setResult({
          hasC2PA: false,
          isValid: false,
          isTrusted: false,
          hasTimestamp: false,
          badgeStatus: 'unsigned',
          formatNote: `Media verified by Verifieddit.com`,
          pendingServerVerification: true,
        })
        setState('verified')
        return
      }

      // --- API-first verification (Verifieddit Cloud Run) ---
      // For files <=10MB, try server-side verification first
      const apiResult = await validateMediaViaApi(file, hash)
      if (apiResult && apiResult.status) {
        const statusToValid: Record<string, boolean> = {
          verified: true, authentic: true, invalid: false, unsigned: false,
        }
        const isValid = statusToValid[apiResult.status] ?? false
        const isTrusted = apiResult.status === 'verified'
        const hasTs = !!apiResult.c2pa?.signedAt

        let badgeStatus: BadgeStatus = 'unsigned'
        if (isValid && isTrusted) badgeStatus = 'trusted'
        else if (isValid) badgeStatus = 'valid'
        else if (apiResult.status === 'invalid') badgeStatus = 'invalid'

        // Validate API AI analysis is informational only — not used for gating
        const metadataAiDetected = false
        // Still run pixel AI detection if available
        let pixelAiResult: AiDetectApiResponse | null = null
        if (isImage && hasConsent) {
          try {
            pixelAiResult = await analyzeImageForAi(file, hash)
          } catch { /* non-blocking */ }
        }
        const pixelAiDetected = pixelAiResult?.aiDetected === true && (pixelAiResult?.confidence ?? 0) >= 0.20

        const verdictResult = determineVerdict({
          metadataAiDetected,
          pixelAiDetected,
          isInvalid: !isValid,
          pixelCheckStatus,
        })

        const verResult: VerificationResult = {
          hasC2PA: apiResult.status !== 'unsigned',
          isValid,
          isTrusted,
          hasTimestamp: hasTs,
          issuer: apiResult.c2pa?.issuer || undefined,
          signedAt: apiResult.c2pa?.signedAt || undefined,
          claimGenerator: apiResult.c2pa?.claimGenerator || undefined,
          badgeStatus,
        }
        setResult(verResult)

        if (verdictResult.verdict === 'fail') {
          let registration: { shortUrl?: string } | null = null
          try {
            registration = await createVerification({
              fileHash: hash,
              filename: file.name,
              c2paStatus: 'rejected',
              c2paIssuer: verResult.issuer,
              c2paSignedAt: verResult.signedAt,
              claimGenerator: verResult.claimGenerator,
              fileType: file.type,
              fileSize: file.size,
              aiDetected: metadataAiDetected || pixelAiDetected,
            })
          } catch (err) {
            console.warn('verifieddit.com unavailable:', err)
          }
          setRejectionDetails({
            reason: metadataAiDetected
              ? 'AI-generated content detected in media metadata'
              : pixelAiDetected
              ? 'AI manipulation detected in image analysis'
              : 'Media integrity verification failed',
            confidence: pixelAiResult?.confidence?.toString() || '',
            generator: pixelAiResult?.detectedGenerator || '',
            badgeUrl: '',
            proofUrl: registration?.shortUrl || '',
          })
          setState('rejected')
          return
        }

        // PASS — register for badge
        setState('verified')
        const thumb = await generateThumbnail(file)
        const statusMap: Record<BadgeStatus, string> = {
          trusted: 'verified', valid: 'authentic', invalid: 'invalid', unsigned: 'unsigned',
        }
        let vr: { shortUrl?: string } | null = null
        try {
          vr = await createVerification({
            fileHash: hash,
            filename: file.name,
            c2paStatus: statusMap[badgeStatus],
            c2paIssuer: verResult.issuer,
            c2paSignedAt: verResult.signedAt,
            claimGenerator: verResult.claimGenerator,
            fileType: file.type,
            fileSize: file.size,
            aiDetected: metadataAiDetected || pixelAiDetected,
            thumbnailUrl: thumb || undefined,
          })
        } catch (err) {
          console.warn('verifieddit.com unavailable:', err)
        }
        if (vr?.shortUrl) setShortUrl(vr.shortUrl)
        return // Skip WASM path — API verification succeeded
      }

      // --- WASM fallback (API unavailable or file >10MB) ---
      // Launch C2PA verification and AI pixel-level detection in parallel
      const c2paPromise = (async () => {
        const { createC2pa } = await import('@contentauth/c2pa-web')

        const c2pa = await createC2pa({
          wasmSrc: '/c2pa.wasm?v=0.6.0-1',
          settings: {
            trust: { trustAnchors: `${location.origin}/c2pa-trust-list.pem` },
          },
        })

        let reader: any = null
        try {
          reader = await c2pa.reader.fromBlob(file.type, file)
        } catch (formatErr: any) {
          const msg = formatErr?.message || ''
          const isUnsupportedFormat = msg.includes('Unsupported') || msg.includes('unsupported')
          const isTooLarge = msg.includes('too large') || msg.includes('Maximum')
          const isInvalidAsset = msg.includes('InvalidAsset') || msg.includes('Box size') || msg.includes('asset bounds')

          if (isUnsupportedFormat || isTooLarge || isInvalidAsset) {
            c2pa.dispose()
            return { type: 'pending-server' as const }
          }

          // InvalidAsset on smaller files: credentials may have been stripped — attempt recovery
          if (isInvalidAsset) {
            c2pa.dispose()
            console.warn('[C2PA] InvalidAsset — attempting credential recovery via Manifest Store')
            const recovery = await attemptCredentialRecovery(hash, file)

            if (recovery.recovered && recovery.manifest) {
              return { type: 'recovered' as const, recovery, manifest: recovery.manifest }
            }

            return { type: 'recovery-failed' as const }
          }

          throw formatErr
        }

        if (!reader) {
          c2pa.dispose()
          return { type: 'unsigned' as const }
        }

        const ms = await reader.manifestStore()
        await reader.free()
        c2pa.dispose()

        if (!ms || !ms.manifests || Object.keys(ms.manifests).length === 0) {
          return { type: 'unsigned' as const }
        }

        return { type: 'manifest' as const, manifestStore: ms }
      })()

      const aiPromise = (isImage && hasConsent)
        ? analyzeImageForAi(file, hash)
        : Promise.resolve(null)

      const [c2paSettled, aiSettled] = await Promise.allSettled([c2paPromise, aiPromise])

      // Handle AI detection result
      if (isImage && hasConsent && aiSettled.status === 'rejected') {
        pixelCheckStatus = 'inconclusive'
      }
      const pixelAiResult = aiSettled.status === 'fulfilled' ? aiSettled.value : null
      if (pixelAiResult) {
        setAiResult(pixelAiResult)
      }

      // Handle C2PA result
      if (c2paSettled.status === 'rejected') {
        throw c2paSettled.reason
      }

      const c2paResult = c2paSettled.value

      if (c2paResult.type === 'pending-server') {
        setResult({
          hasC2PA: false,
          isValid: false,
          isTrusted: false,
          hasTimestamp: false,
          badgeStatus: 'unsigned',
          formatNote: `Media verified by Verifieddit.com`,
          pendingServerVerification: true,
        })
        setState('verified')
        return
      }

      if (c2paResult.type === 'recovered') {
        const m = c2paResult.manifest
        const ecosystem = isEcosystemSigner(m.signerCn)
        setResult({
          hasC2PA: true,
          isValid: true,
          isTrusted: ecosystem,
          hasTimestamp: !!m.signedAt,
          issuer: m.signerCn || undefined,
          signedAt: m.signedAt || undefined,
          badgeStatus: ecosystem ? 'trusted' : 'valid',
          formatNote: c2paResult.recovery.explanatoryNote,
        })
        setState('verified')

        // Register with verifieddit
        const thumb = await generateThumbnail(file)
        const vr = await createVerification({
          fileHash: hash,
          filename: file.name,
          c2paStatus: ecosystem ? 'verified' : 'authentic',
          c2paIssuer: m.signerCn || undefined,
          c2paSignedAt: m.signedAt || undefined,
          fileType: file.type,
          fileSize: file.size,
          thumbnailUrl: thumb || undefined,
        })
        if (vr?.shortUrl) setShortUrl(vr.shortUrl)
        return
      }

      if (c2paResult.type === 'recovery-failed') {
        setResult({
          hasC2PA: false,
          isValid: false,
          isTrusted: false,
          hasTimestamp: false,
          badgeStatus: 'unsigned',
          formatNote: 'This file may have had its credentials removed by a platform or file conversion. ' +
            'No matching credentials were found in the recovery database. ' +
            'Server-side verification will be performed after upload.',
        })
        setState('verified')
        return
      }

      if (c2paResult.type === 'unsigned') {
        setResult({ hasC2PA: false, isValid: false, isTrusted: false, hasTimestamp: false, badgeStatus: 'unsigned' })
        setState('verified')
        return
      }

      // c2paResult.type === 'manifest'
      const ms = c2paResult.manifestStore

      // Store manifest store for provenance diagram
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setManifestStore(ms as any)

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const activeManifest = (ms.manifests as any)[ms.active_manifest ?? ''] ?? Object.values(ms.manifests)[0]

      if (!activeManifest) {
        setResult({ hasC2PA: false, isValid: false, isTrusted: false, hasTimestamp: false, badgeStatus: 'unsigned' })
        setState('verified')
        return
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const c2paDetails = extractDeepC2PAMetadata(ms as any, activeManifest)
      const validationResults = c2paDetails.validationResults || undefined
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const signatureInfo = (activeManifest as any).signature_info || null

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const isValid = calculateTrueValidationStatus(validationResults, signatureInfo as any)
      const isTrusted = isSignerTrusted(validationResults)
      const hasTs = hasValidTimestamp(validationResults)

      const claimGen = activeManifest.claim_generator || (activeManifest as any).claimGenerator
      const claimGeneratorStr = typeof claimGen === 'string' ? claimGen : claimGen?.name || null
      const assertions = activeManifest.assertions || []
      const aiDetection = detectAiContent(claimGeneratorStr, assertions)

      // Use determineVerdict to compute final pass/fail
      const metadataAiDetected = aiDetection.isAiGenerated
      const pixelAiDetected = pixelAiResult?.aiDetected === true && (pixelAiResult?.confidence ?? 0) >= 0.20
      const verdictResult = determineVerdict({
        metadataAiDetected,
        pixelAiDetected,
        isInvalid: !isValid,
        pixelCheckStatus,
      })

      let badgeStatus: BadgeStatus = 'unsigned'
      if (isValid && isTrusted) badgeStatus = 'trusted'
      else if (isValid) badgeStatus = 'valid'
      else badgeStatus = 'invalid'

      const verResult: VerificationResult = {
        hasC2PA: true,
        isValid,
        isTrusted,
        hasTimestamp: hasTs,
        issuer: signatureInfo?.issuer || signatureInfo?.common_name,
        signedAt: signatureInfo?.time,
        claimGenerator: claimGeneratorStr,
        badgeStatus,
        aiDetection: aiDetection.isAiGenerated ? { isAiGenerated: true, note: aiDetection.note, detectedTools: aiDetection.detectedTools, digitalSourceType: aiDetection.digitalSourceType } : undefined,
        validationResults,
      }
      setResult(verResult)

      if (verdictResult.verdict === 'fail') {
        // Register with verifieddit — non-blocking, best-effort
        let registration: { shortUrl?: string } | null = null
        try {
          registration = await createVerification({
            fileHash: hash,
            filename: file.name,
            c2paStatus: 'rejected',
            c2paIssuer: verResult.issuer,
            c2paSignedAt: verResult.signedAt,
            claimGenerator: claimGeneratorStr || undefined,
            fileType: file.type,
            fileSize: file.size,
            aiDetected: metadataAiDetected || pixelAiDetected,
          })
        } catch (err) {
          console.warn('verifieddit.com unavailable, proceeding without badge:', err)
        }

        setRejectionDetails({
          reason: metadataAiDetected
            ? 'AI-generated content detected in media metadata'
            : pixelAiDetected
            ? 'AI manipulation detected in image analysis'
            : 'Media integrity verification failed',
          confidence: pixelAiResult?.confidence?.toString() || '',
          generator: pixelAiResult?.detectedGenerator || '',
          badgeUrl: '',
          proofUrl: registration?.shortUrl || '',
        })

        setState('rejected')
        return
      }

      // PASS flow
      setState('verified')

      // Generate thumbnail (non-blocking) and register with verifieddit
      const thumb = await generateThumbnail(file)

      const statusMap: Record<BadgeStatus, string> = {
        trusted: 'verified', valid: 'authentic', invalid: 'invalid', unsigned: 'unsigned',
      }
      let vr: { shortUrl?: string } | null = null
      try {
        vr = await createVerification({
          fileHash: hash,
          filename: file.name,
          c2paStatus: statusMap[badgeStatus],
          c2paIssuer: verResult.issuer,
          c2paSignedAt: verResult.signedAt,
          claimGenerator: claimGeneratorStr || undefined,
          fileType: file.type,
          fileSize: file.size,
          aiDetected: metadataAiDetected || pixelAiDetected,
          thumbnailUrl: thumb || undefined,
        })
      } catch (err) {
        console.warn('verifieddit.com unavailable, proceeding without badge:', err)
      }
      if (vr?.shortUrl) setShortUrl(vr.shortUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed')
      setState('error')
    }
  }, [file])

  const upload = useCallback(async () => {
    if (!file || !password) return
    setState('uploading')
    setError('')
    setUploadProgress(0)

    try {
      const response = await fetch(UPLOAD_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          fileHash: fileHash || '',
          email: uploaderEmail,
          password,
          newsletterConsent,
        }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || `Upload request failed (${response.status})`)
      }

      const data = await response.json()

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100))
          }
        })
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve()
          else reject(new Error(`Upload failed (${xhr.status})`))
        })
        xhr.addEventListener('error', () => reject(new Error('Upload failed')))
        xhr.open('PUT', data.uploadUrl)
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.send(file)
      })

      // Gate success on confirm-upload — Firestore/Nest sync depends on this
      if (data.key && fileHash) {
        const confirmRes = await fetch(`${API_BASE}/api/drop/confirm-upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileHash, key: data.key }),
        })

        if (!confirmRes.ok) {
          const confirmErr = await confirmRes.json().catch(() => ({}))
          throw new Error(confirmErr.error || 'Upload confirmation failed — please try again')
        }
      }

      setState('uploaded')

      // Server-side C2PA verification for large/MOV files
      if (result?.pendingServerVerification && data.downloadUrl) {
        try {
          const verifyRes = await fetch(`${API_BASE}/api/drop/verify-server`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              url: data.downloadUrl,
              filename: file.name,
              fileHash: fileHash || undefined,
            }),
          })
          if (verifyRes.ok) {
            const serverResult = await verifyRes.json()
            if (serverResult.hasC2PA) {
              setResult(prev => prev ? {
                ...prev,
                hasC2PA: true,
                isValid: serverResult.isValid,
                isTrusted: serverResult.isTrusted,
                issuer: serverResult.issuer,
                signedAt: serverResult.signedAt,
                claimGenerator: serverResult.claimGenerator,
                badgeStatus: serverResult.isTrusted ? 'trusted' : serverResult.isValid ? 'valid' : 'invalid',
                formatNote: 'Verified by thephenom.ai',
                pendingServerVerification: false,
              } : prev)
            } else {
              setResult(prev => prev ? {
                ...prev,
                formatNote: 'Media verified by Verifieddit.com',
                pendingServerVerification: false,
              } : prev)
            }
          }
        } catch (verifyErr) {
          console.warn('[C2PA] Server-side verification failed:', verifyErr)
          setResult(prev => prev ? { ...prev, pendingServerVerification: false } : prev)
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
      setState('error')
    }
  }, [file, password, uploaderEmail, newsletterConsent, result, fileHash])

  const sendPasscode = useCallback(async () => {
    if (!uploaderEmail.includes('@')) return
    setSendingPasscode(true)
    try {
      const res = await fetch(`${API_BASE}/api/drop/send-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: uploaderEmail, fileHash: fileHash || '' }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setError(data.error || 'Failed to send passcode')
        return
      }
      setPasscodeSent(true)
    } catch {
      setError('Failed to send passcode. Please try again.')
    } finally {
      setSendingPasscode(false)
    }
  }, [uploaderEmail, fileHash])

  const reset = useCallback(() => {
    setFile(null)
    setResult(null)
    setState('idle')
    setError('')
    setPassword('')
    setPasscodeSent(false)
    setSendingPasscode(false)
    setUploadProgress(0)
    setFileHash(null)
    setShortUrl(null)
    setManifestStore(null)
    setRejectionDetails(null)
    setRejectionEmail('')
    setRejectionEmailSent(false)
    setIsExistingUser(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const isAiBlocked = result?.aiDetection?.isAiGenerated === true || (aiResult?.aiDetected === true && (aiResult?.confidence ?? 0) >= 0.20)
  const isMovFormat = file?.type === 'video/quicktime' || file?.name?.toLowerCase().endsWith('.mov')
  const needsServerSideVerification = !!(result?.formatNote) // MOV or >1GB — server-side only
  // Allow upload if: (C2PA valid) OR (needs server-side verification — MOV or >1GB)
  const canUpload = result && !isAiBlocked && password.length > 0 && uploaderEmail.includes('@') && isExistingUser !== null && passcodeSent

  // Server-side AI detection for all images
  const isImage = result && file?.type?.startsWith('image/')
  const showAiConsent = isImage && !hasAiDetectConsent() && !aiResult && !aiAnalyzing

  const [aiAttempted, setAiAttempted] = useState(false)

  async function runAiDetection() {
    if (!file || !fileHash) return
    setAiAnalyzing(true)
    setAiAttempted(true)
    try {
      const apiResult = await analyzeImageForAi(file, fileHash)
      setAiResult(apiResult)
    } finally {
      setAiAnalyzing(false)
    }
  }

  // Auto-run if consent was previously given (for cases not handled in verify())
  // aiAttempted prevents infinite retry on failure
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shouldAutoRunAi = !!(isImage && hasAiDetectConsent() && !aiResult && !aiAnalyzing && !aiAttempted && fileHash)
  useEffect(() => {
    if (shouldAutoRunAi) runAiDetection()
  }, [shouldAutoRunAi]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex flex-col">
      {/* Phenom Website Header */}
      <header style={{ background: '#151515', borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '16px 0', textAlign: 'center' }}>
        <img src="/assets/images/cropped-logo_small.png" alt="The Phenom App Logo" style={{ height: '50px', margin: '0 auto 6px', display: 'block' }} />
        <h1 style={{ fontFamily: "'Oswald', Impact, sans-serif", fontSize: '28px', fontWeight: 700, color: '#d73429', margin: 0 }}>The Phenom App</h1>
        <nav className="phenom-nav" style={{ marginTop: '6px' }}>
          <ul>
            <li><a href="/">Home</a></li>
            <li><a href="/about.html">About</a></li>
            <li><a href="/app.html">App</a></li>
            <li><a href="/drop/">Drop</a></li>
            <li><a href="/shop.html">Shop</a></li>
            <li><a href="/media.html">In the Media</a></li>
            <li><a href="/blog.html">Blog</a></li>
            <li><a href="/faq.html">FAQ</a></li>
            <li><a href="/support.html">Support</a></li>
          </ul>
        </nav>
      </header>

      {/* Main */}
      <main className="flex-1 px-10 md:px-20 py-10">
        <div className="max-w-6xl mx-auto flex gap-8">
          {/* Left column — main content */}
          <div className="flex-1 space-y-8">

          {/* Step 1: Select file */}
          <section className="animate-fadeIn">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-6 rounded-full bg-phenom-red text-white text-xs font-bold flex items-center justify-center">1</span>
              <h2 className="text-sm font-semibold text-phenom-gray uppercase tracking-wider">Select Media</h2>
            </div>
            <div
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-phenom p-10 text-center cursor-pointer transition-all ${
                dragOver
                  ? 'border-phenom-cyan bg-phenom-cyan/5'
                  : file
                    ? 'border-green-500/40 bg-green-900/10'
                    : 'border-phenom-gray/30 hover:border-phenom-cyan/50 bg-phenom-dark/30'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*,video/*"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) handleFileSelect(f)
                }}
              />
              {file ? (
                <div className="space-y-1">
                  <p className="text-white font-medium">{file.name}</p>
                  <p className="text-phenom-gray text-sm">{formatSize(file.size)} &middot; {file.type}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-phenom-gray">Drop your media file here or click to browse</p>
                  <p className="text-phenom-gray/50 text-xs">Supports images and video (max {formatFileSize(MAX_FILE_SIZE)})</p>
                </div>
              )}
            </div>
          </section>

          {/* Step 2: Verify */}
          {file && (
            <section className="animate-slideUp">
              <div className="flex items-center gap-2 mb-3">
                <span className={`w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center ${result ? 'bg-green-600' : 'bg-phenom-red'}`}>2</span>
                <h2 className="text-sm font-semibold text-phenom-gray uppercase tracking-wider">Verify File Details</h2>
              </div>

              {!result && state !== 'verifying' && (
                <button
                  onClick={verify}
                  className="w-full py-3 bg-phenom-red hover:bg-phenom-red-dark text-white font-semibold rounded-phenom transition-colors"
                >
                  Verify File Details
                </button>
              )}

              {state === 'verifying' && (
                <div className="bg-[#1a1a1a] rounded-xl px-4 py-2.5 flex items-center gap-3 animate-fadeIn">
                  <div className="w-7 h-7 rounded-full border-2 flex items-center justify-center" style={{ borderColor: 'rgba(165,227,232,0.19)' }}>
                    <svg className="w-4 h-4 animate-spin" style={{ color: '#a5e3e8' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10" />
                    </svg>
                  </div>
                  <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', fontWeight: 600, color: '#a5e3e8' }}>thephenom.ai is processing</span>
                  <div className="w-20 h-[3px] rounded-sm overflow-hidden" style={{ background: '#151515' }}>
                    <div className="h-full rounded-sm" style={{ width: '60%', background: 'linear-gradient(90deg, #06b6d4, #8b5cf6, #06b6d4)', backgroundSize: '200% 100%', animation: 'progressSlide 2s ease-in-out infinite' }} />
                  </div>
                  <style>{`
                    @keyframes progressSlide { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                  `}</style>
                </div>
              )}

              {result && <VerificationCard result={result} />}
            </section>
          )}

          {/* Provenance Chain Diagram */}
          {result?.hasC2PA && manifestStore && (
            <ProvenanceDiagram manifestStore={manifestStore} className="animate-slideUp" />
          )}

          {/* Server-side AI Detection for images */}
          {isImage && (
            <AiDetectionBanner
              showConsent={!!showAiConsent}
              onConsent={runAiDetection}
              analyzing={aiAnalyzing}
              result={aiResult}
            />
          )}

          {/* AI Content Block Gate */}
          {result?.hasC2PA && result?.isValid && isAiBlocked && (
            <section className="animate-slideUp">
              <div className="bg-red-950/40 border-2 border-red-500 rounded-phenom p-8 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-red-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-red-400 text-lg font-bold">AI-Generated Content Blocked</h3>
                    <p className="text-red-300/80 text-sm mt-1">
                      This media was identified as AI-generated content. AI-generated files cannot be submitted to Phenom Drop for UAP evidence. Only authentic, device-captured media is accepted.
                    </p>
                  </div>
                </div>
                <div className="bg-red-900/30 rounded-lg px-4 py-3 space-y-2">
                  {result.aiDetection?.detectedTools && result.aiDetection.detectedTools.length > 0 && (
                    <div className="text-sm text-red-300">
                      <span className="font-semibold text-red-400">AI Source: </span>
                      {result.aiDetection.detectedTools.join(', ')}
                    </div>
                  )}
                  {result.aiDetection?.digitalSourceType && (
                    <div className="text-sm text-red-300">
                      <span className="font-semibold text-red-400">Source Type: </span>
                      {result.aiDetection.digitalSourceType}
                    </div>
                  )}
                  {!result.aiDetection?.detectedTools?.length && !result.aiDetection?.digitalSourceType && (
                    <div className="text-sm text-red-300">
                      <span className="font-semibold text-red-400">Detected: </span>
                      {result.aiDetection?.note}
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Rejection Panel — FAIL verdict */}
          {state === 'rejected' && rejectionDetails && (
            <section className="animate-slideUp">
              <div className="bg-red-950/40 border-2 border-red-500 rounded-phenom p-8 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-red-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-red-400 text-lg font-bold">
                      {rejectionDetails.reason.includes('AI') ? 'AI Content Detected' : 'Verification Failed'}
                    </h3>
                    <p className="text-red-300/80 text-sm mt-1">{rejectionDetails.reason}</p>
                  </div>
                </div>

                {(rejectionDetails.reason.includes('AI') && (rejectionDetails.confidence || rejectionDetails.generator)) && (
                  <div className="bg-red-900/30 rounded-lg px-4 py-3 space-y-2">
                    {rejectionDetails.confidence && (
                      <p className="text-sm text-red-300">
                        <span className="font-semibold text-red-400">Confidence: </span>
                        {(parseFloat(rejectionDetails.confidence) * 100).toFixed(1)}%
                      </p>
                    )}
                    {rejectionDetails.generator && (
                      <p className="text-sm text-red-300">
                        <span className="font-semibold text-red-400">Detected: </span>
                        {rejectionDetails.generator}
                      </p>
                    )}
                  </div>
                )}

                <p className="text-phenom-gray/70 text-sm">
                  This file cannot be uploaded. Enter your email to receive a verification badge.
                </p>

                {!rejectionEmailSent ? (
                  <div className="space-y-3">
                    <input
                      type="email"
                      value={rejectionEmail}
                      onChange={(e) => setRejectionEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full bg-phenom-surface border border-phenom-gray/20 rounded-lg px-4 py-3 text-white placeholder-phenom-gray/50 focus:outline-none focus:border-phenom-cyan/50"
                    />
                    <button
                      onClick={async () => {
                        if (!rejectionEmail.includes('@')) return
                        try {
                          await fetch(`${API_BASE}/api/drop/send-rejection`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              email: rejectionEmail,
                              fileHash,
                              rejectionReason: rejectionDetails.reason,
                              aiConfidence: rejectionDetails.confidence,
                              detectedGenerator: rejectionDetails.generator,
                              badgeUrl: rejectionDetails.badgeUrl,
                              proofUrl: rejectionDetails.proofUrl,
                            }),
                          })
                        } catch (err) {
                          console.warn('Failed to send rejection email:', err)
                        }
                        setRejectionEmailSent(true)
                      }}
                      disabled={!rejectionEmail.includes('@')}
                      className={`w-full py-3 font-semibold rounded-phenom transition-colors ${
                        rejectionEmail.includes('@')
                          ? 'bg-phenom-red hover:bg-phenom-red-dark text-white'
                          : 'bg-phenom-surface text-phenom-gray/50 cursor-not-allowed'
                      }`}
                    >
                      Send Badge
                    </button>
                  </div>
                ) : (
                  <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 text-center">
                    <p className="text-green-400 font-medium">Verification badge sent to {rejectionEmail}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Server-Side Verification Notice (MOV format or file >1GB) */}
          {result && needsServerSideVerification && !result.hasC2PA && (
            <section className="animate-slideUp">
              <div className="bg-phenom-dark rounded-phenom p-6 border border-amber-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{isMovFormat ? '🎬' : '📦'}</span>
                  <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">
                    {isMovFormat ? 'MOV Format Detected' : 'Large File Detected'}
                  </h3>
                </div>
                <p className="text-sm text-phenom-gray leading-relaxed">
                  {result.formatNote}
                </p>
                <p className="text-xs text-phenom-gray/60 mt-2">
                  You may proceed with the upload. Verification results will appear on N.E.S.T. after processing.
                </p>
              </div>
            </section>
          )}

          {/* Verification Proof (badge + certificate) */}
          {result?.hasC2PA && result?.isValid && (
            <section className="animate-slideUp">
              <div className="flex items-center gap-2 mb-3">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a5e3e8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
                <h2 className="text-sm font-semibold text-phenom-gray uppercase tracking-wider">Verification Proof</h2>
              </div>
              <div className="bg-phenom-dark rounded-phenom p-6 space-y-4">
                <div className="flex items-center gap-4">
                  <img
                    src={svgToDataUri(generateBadgeSvg(result.badgeStatus, 'classic', file?.name))}
                    alt="Verification badge"
                    className="h-16 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-phenom-gray text-sm">
                      Your verified media has been registered with{' '}
                      <a href="https://verifieddit.com" target="_blank" rel="noopener noreferrer" className="text-phenom-cyan hover:underline">verifieddit.com</a>
                    </p>
                    {shortUrl && (
                      <p className="text-phenom-cyan/70 text-xs mt-1 truncate">
                        Proof: <a href={shortUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">{shortUrl}</a>
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      const svg = generateBadgeSvg(result.badgeStatus, 'classic', file?.name)
                      const url = svgToBlobUrl(svg)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `${file?.name?.replace(/\.[^.]+$/, '') || 'media'}-verifieddit-badge.svg`
                      a.click()
                      setTimeout(() => URL.revokeObjectURL(url), 1000)
                    }}
                    className="flex-1 py-2.5 bg-phenom-surface hover:bg-phenom-gray/20 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Badge
                  </button>
                  <button
                    onClick={() => {
                      if (!file) return
                      exportVerificationCertificate({
                        name: file.name,
                        type: file.type,
                        size: file.size,
                        hasC2PA: true,
                        fileHash: fileHash || undefined,
                        shortUrl: shortUrl || undefined,
                        aiDetected: result.aiDetection,
                        c2paDetails: {
                          hasValidSignature: result.isValid,
                          hasTrustedSigner: result.isTrusted,
                          hasErrors: result.badgeStatus === 'invalid',
                          hasWarnings: !result.isTrusted && result.isValid,
                          activeManifest: {
                            claimGenerator: result.claimGenerator,
                            signatureInfo: result.issuer ? { issuer: result.issuer, time: result.signedAt } : null,
                          },
                        },
                      })
                    }}
                    className="flex-1 py-2.5 bg-phenom-surface hover:bg-phenom-gray/20 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                    </svg>
                    Certificate
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Step 3: Upload (only if verified + not AI blocked) */}
          {result && !isAiBlocked && state !== 'rejected' && (
            <section className="animate-slideUp">
              <div className="flex items-center gap-2 mb-3">
                <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${result?.isValid || needsServerSideVerification ? 'bg-[#d73429] text-white' : 'bg-[#151515] text-[#aab1be80]'}`}>2</span>
                <h2 className={`text-sm font-semibold uppercase tracking-wider ${result?.isValid || needsServerSideVerification ? 'text-phenom-gray' : 'text-phenom-gray/50'}`}>Upload</h2>
              </div>

              <div className="bg-phenom-dark rounded-phenom p-6 space-y-4">
                {/* Email info note */}
                <div className="flex gap-2.5 rounded-lg p-3" style={{ background: 'rgba(165,227,232,0.05)', border: '1px solid rgba(165,227,232,0.13)' }}>
                  <svg className="w-4 h-4 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="#a5e3e8" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '11px', lineHeight: 1.5, color: 'rgba(165,227,232,0.8)' }}>
                    Your email is required to receive your secure upload passcode and helps us minimize abuse. We may also send occasional Phenom news — you can unsubscribe anytime.
                  </p>
                </div>

                {/* User type radio — skipped when identity is carried in from /contact */}
                {!identityHydrated && (
                  <div className="space-y-3">
                    <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '13px', fontWeight: 500, color: '#fff' }}>
                      Are you already a user of The Phenom App?
                    </p>
                    <div className="flex gap-6">
                      {['Yes', 'No'].map((option) => (
                        <label key={option} className="flex items-center gap-2 cursor-pointer" onClick={() => setIsExistingUser(option === 'Yes')}>
                          <div className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center ${
                            isExistingUser === (option === 'Yes') ? 'border-[#a5e3e8]' : 'border-[#aab1be40]'
                          }`}>
                            {isExistingUser === (option === 'Yes') && <div className="w-2 h-2 rounded-full bg-[#a5e3e8]" />}
                          </div>
                          <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '13px', fontWeight: 500, color: isExistingUser === (option === 'Yes') ? '#fff' : '#aab1be' }}>{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Identity confirmation block — shown when arriving from /contact */}
                {identityHydrated && (
                  <div className="rounded-lg p-3 border" style={{ background: '#151515', borderColor: 'rgba(165,227,232,0.3)' }}>
                    <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '10px', color: 'rgba(170,177,190,0.6)', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Continuing as</p>
                    <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '14px', fontWeight: 600, color: '#fff', margin: '4px 0 2px' }}>{identityName || uploaderEmail}</p>
                    <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '11px', color: '#aab1be', margin: 0 }}>{uploaderEmail} · {isExistingUser ? 'existing user' : 'new to Phenom'}</p>
                  </div>
                )}

                {/* Email field + Send Passcode button.
                    - Hydrated path (visit from /contact): no email input,
                      identity already shown in 'Continuing as' card. Just
                      a full-width 'Send passcode to ma•••@example.com'.
                    - Direct path (fallback only — bare visits get bounced
                      to /contact by the guard): full email entry UI. */}
                {isExistingUser !== null && (
                  <div className="space-y-2 animate-fadeIn">
                    {!identityHydrated && (
                      <>
                        <label style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', fontWeight: 500, color: '#aab1be' }}>
                          {isExistingUser ? 'Phenom App email address' : 'Email address'}
                        </label>
                        {isExistingUser && (
                          <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '10px', color: 'rgba(170,177,190,0.38)' }}>
                            Enter the email you registered with in the app
                          </p>
                        )}
                      </>
                    )}
                    <div className="flex gap-2">
                      {!identityHydrated && (
                        <input
                          type="email"
                          placeholder="you@example.com"
                          value={uploaderEmail}
                          onChange={(e) => { setUploaderEmail(e.target.value); setPasscodeSent(false) }}
                          className="flex-1 bg-[#151515] border rounded-lg px-4 py-3 text-white placeholder-phenom-gray/50 focus:outline-none"
                          style={{ borderColor: 'rgba(170,177,190,0.19)', fontFamily: "'Roboto Mono', monospace", fontSize: '14px' }}
                          disabled={state === 'uploading' || state === 'uploaded'}
                        />
                      )}
                      <button
                        onClick={sendPasscode}
                        disabled={!uploaderEmail.includes('@') || sendingPasscode || state === 'uploading' || state === 'uploaded'}
                        className={`${identityHydrated ? 'flex-1 ' : ''}px-4 py-3 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                          uploaderEmail.includes('@') && !sendingPasscode
                            ? passcodeSent
                              ? 'bg-green-800/30 border border-green-500/30 text-green-400'
                              : 'bg-phenom-red hover:bg-phenom-red-dark text-white'
                            : 'bg-phenom-surface text-phenom-gray/50 cursor-not-allowed'
                        }`}
                      >
                        {sendingPasscode
                          ? 'Sending…'
                          : passcodeSent
                            ? `Sent to ${uploaderEmail.replace(/^(.{2}).*(@.+)$/, '$1•••$2')}`
                            : `Send passcode${identityHydrated ? ' to ' + uploaderEmail.replace(/^(.{2}).*(@.+)$/, '$1•••$2') : ''}`}
                      </button>
                    </div>
                    {passcodeSent && (
                      <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '11px', color: 'rgba(74,222,128,0.8)' }}>
                        Passcode sent — check your inbox
                      </p>
                    )}
                  </div>
                )}

                {/* Passcode field — shown after code is sent */}
                {passcodeSent && (
                  <div className="relative animate-fadeIn">
                    <input
                      type="password"
                      placeholder="Enter passcode"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-phenom-surface border border-phenom-gray/20 rounded-lg px-4 py-3 text-white placeholder-phenom-gray/50 focus:outline-none focus:border-phenom-cyan/50"
                      disabled={state === 'uploading' || state === 'uploaded'}
                    />
                  </div>
                )}

                {state === 'uploading' && (
                  <div className="space-y-2">
                    <div className="w-full bg-phenom-surface rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-phenom-cyan h-full rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-phenom-gray text-sm text-center">{uploadProgress}% uploaded</p>
                  </div>
                )}

                {state === 'uploaded' && (
                  <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 text-center">
                    <p className="text-green-400 font-medium">Upload complete! Your verified media has been submitted.</p>
                  </div>
                )}

                {state !== 'uploading' && state !== 'uploaded' && (
                  <button
                    onClick={upload}
                    disabled={!canUpload}
                    className={`w-full py-3 font-semibold rounded-phenom transition-colors ${
                      canUpload
                        ? 'bg-phenom-red hover:bg-phenom-red-dark text-white'
                        : 'bg-phenom-surface text-phenom-gray/50 cursor-not-allowed'
                    }`}
                  >
                    Upload Verified Media
                  </button>
                )}
              </div>
            </section>
          )}

          {/* Info message for unsigned files */}
          {result && !result.hasC2PA && state !== 'rejected' && (
            <section className="animate-slideUp">
              <div className="bg-amber-900/10 border border-amber-500/20 rounded-phenom p-4 text-center space-y-1">
                <p className="text-amber-400/80 text-sm font-medium">No provenance detected</p>
                <p className="text-phenom-gray/60 text-xs">This file has no provenance. It can still be uploaded.</p>
              </div>
            </section>
          )}

          {/* Error display */}
          {error && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-phenom p-4 text-center space-y-3">
              <p className="text-red-400 text-sm">{error}</p>
              {result && state === 'error' && (
                <button
                  onClick={() => { setState('verified'); setError(''); }}
                  className="px-6 py-2 bg-phenom-red hover:bg-phenom-red-dark text-white text-sm font-semibold rounded-phenom transition-colors"
                >
                  Try Again
                </button>
              )}
            </div>
          )}

          {/* Reset */}
          {(result || error) && (
            <button
              onClick={reset}
              className="text-phenom-gray hover:text-white text-sm underline underline-offset-4 transition-colors"
            >
              Start over with a different file
            </button>
          )}
          </div>

          {/* Right sidebar — only shows after file selected */}
          {file && (
            <div className="w-[300px] flex-shrink-0 space-y-4" style={{ paddingTop: '44px' }}>
              {/* FILE DETAILS card */}
              <div className="bg-[#1a1a1a] rounded-xl p-5 space-y-3">
                <h3 className="text-[11px] font-semibold tracking-[2px] uppercase" style={{ color: '#aab1be', fontFamily: "'Roboto Mono', monospace" }}>File Details</h3>
                <div className="h-px" style={{ background: 'rgba(170,177,190,0.13)' }} />
                {[
                  ['Filename', file.name],
                  ['Size', formatFileSize(file.size)],
                  ['Type', file.type],
                  ['SHA-256', fileHash ? `${fileHash.substring(0,4)}...${fileHash.substring(fileHash.length-4)}` : '—'],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between">
                    <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', color: 'rgba(170,177,190,0.38)' }}>{label}</span>
                    <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', fontWeight: 500, color: label === 'SHA-256' ? '#a5e3e8' : '#fff' }}>{value}</span>
                  </div>
                ))}
              </div>

              {/* VERIFICATION STATUS card — only after verification.
                  When the file has no C2PA metadata, signature/trust/timestamp
                  checks are not applicable — show a neutral "No provenance"
                  state and suppress the irrelevant pills entirely. */}
              {result && (
                <div className="bg-[#1a1a1a] rounded-xl p-5 space-y-3">
                  <h3 className="text-[11px] font-semibold tracking-[2px] uppercase" style={{ color: '#aab1be', fontFamily: "'Roboto Mono', monospace" }}>Verification Status</h3>
                  <div className="h-px" style={{ background: 'rgba(170,177,190,0.13)' }} />
                  {!result.hasC2PA ? (
                    <div className="flex items-center gap-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-gray-500" />
                      <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', fontWeight: 600, color: '#aab1be' }}>
                        No provenance metadata found
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full ${result.isTrusted ? 'bg-green-500' : result.isValid ? 'bg-yellow-500' : 'bg-red-500'}`} />
                        <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', fontWeight: 600, color: result.isTrusted ? '#22c55e' : result.isValid ? '#eab308' : '#ef4444' }}>
                          {result.isTrusted ? 'Verified — Trusted Signer' : result.isValid ? 'Valid — Untrusted Signer' : 'Invalid Signature'}
                        </span>
                      </div>
                      <div className="flex gap-1.5">
                        {[
                          ['Signature', result.isValid],
                          ['Trusted', result.isTrusted],
                          ['Timestamp', result.hasTimestamp],
                        ].map(([label, ok]) => (
                          <div key={label as string} className="flex-1 rounded-lg py-2 text-center" style={{ background: ok ? 'rgba(20,83,45,0.1)' : '#151515' }}>
                            <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '11px', color: ok ? '#4ade80' : 'rgba(170,177,190,0.3)' }}>
                              {ok ? '✓' : '—'} {label as string}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* CAPTURE LOCATION map — shown when EXIF GPS data is found.
                  Independent of provenance state: a file can have GPS but no
                  C2PA, and vice versa. */}
              {gpsCoords && (
                <GPSMap lat={gpsCoords.lat} lon={gpsCoords.lon} fileName={file?.name} />
              )}

              {/* PROVENANCE card — only if C2PA data */}
              {result?.hasC2PA && (
                <div className="bg-[#1a1a1a] rounded-xl p-5 space-y-3">
                  <h3 className="text-[11px] font-semibold tracking-[2px] uppercase" style={{ color: '#aab1be', fontFamily: "'Roboto Mono', monospace" }}>Provenance</h3>
                  <div className="h-px" style={{ background: 'rgba(170,177,190,0.13)' }} />
                  {[
                    ['Issuer', result.issuer || '—'],
                    ['Signed', result.signedAt || '—'],
                    ['Generator', result.claimGenerator || '—'],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between">
                      <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', color: 'rgba(170,177,190,0.38)' }}>{label}</span>
                      <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', fontWeight: 500, color: '#fff' }}>{value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Phenom Website Footer */}
      <footer style={{ background: '#151515', borderTop: '1px solid rgba(255,255,255,0.1)', padding: '12px 0', textAlign: 'center' }}>
        <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '16px', color: '#fff', margin: 0 }}>&copy; 2026 Phenom LLC. All rights reserved.</p>
        <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '16px', margin: '4px 0 0' }}>
          <a href="/eula.html" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>EULA</a>
          {' | '}
          <a href="/privacy-policy.html" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Privacy Policy</a>
          {' | '}
          <a href="/support.html" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Support</a>
        </p>
        <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', color: 'rgba(170,177,190,0.38)', marginTop: '8px' }}>
          Powered by <a href="https://verifieddit.com" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(165,227,232,0.44)' }}>Verifieddit</a> verification
        </p>
      </footer>
    </div>
  )
}

function VerificationCard({ result }: { result: VerificationResult }) {
  const badgeSvg = generateBadgeSvg(result.badgeStatus, 'classic', undefined)
  const badgeUri = svgToDataUri(badgeSvg)
  const ecosystem = isEcosystemSigner(result.issuer ?? null)

  const statusColor = result.badgeStatus === 'trusted'
    ? 'border-green-500/40 bg-green-900/10'
    : result.badgeStatus === 'valid'
      ? 'border-amber-500/40 bg-amber-900/10'
      : result.badgeStatus === 'invalid'
        ? 'border-red-500/40 bg-red-900/10'
        : 'border-phenom-gray/30 bg-phenom-dark/30'

  // Ecosystem signers get branded language (no C2PA terminology)
  const statusLabel = ecosystem
    ? 'Verified Authentic'
    : result.badgeStatus === 'trusted' ? 'Verified'
    : result.badgeStatus === 'unsigned' ? 'No provenance metadata found'
    : result.badgeStatus

  return (
    <div className={`border rounded-phenom p-6 space-y-4 ${statusColor}`}>
      <div className="flex items-start gap-4">
        <img src={badgeUri} alt={result.badgeStatus} className="h-14 flex-shrink-0" />
        <div className="space-y-1 flex-1 min-w-0">
          <p className="text-white font-semibold capitalize">{statusLabel}</p>
          {result.issuer && (
            <p className="text-phenom-gray text-sm truncate">
              {ecosystem ? 'Verified by' : 'Signed by'}: {result.issuer}
            </p>
          )}
          {result.signedAt && (
            <p className="text-phenom-gray text-sm">
              {ecosystem ? 'Captured' : 'Signed'}: {new Date(result.signedAt).toLocaleString()}
            </p>
          )}
          {result.claimGenerator && !ecosystem && (
            <p className="text-phenom-gray text-sm truncate">Tool: {result.claimGenerator}</p>
          )}
        </div>
      </div>

      {/* Recovery note */}
      {result.formatNote && result.hasC2PA && (
        <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg px-4 py-3">
          <p className="text-amber-300/80 text-xs leading-relaxed">{result.formatNote}</p>
        </div>
      )}

      {/* Status details — ecosystem signers get user-friendly labels.
          Hidden when no provenance is present: signature/trust/timestamp
          checks are not applicable to an unsigned file. */}
      {result.hasC2PA && (
        <div className="grid grid-cols-3 gap-3 text-xs">
          <StatusPill label={ecosystem ? 'Authentic' : 'Signature'} ok={result.isValid} />
          <StatusPill label={ecosystem ? 'Verified' : 'Trusted'} ok={result.isTrusted} />
          <StatusPill label="Timestamp" ok={result.hasTimestamp} />
        </div>
      )}

      {/* AI detection warning */}
      {result.aiDetection && (
        <div className="bg-red-900/30 border border-red-500/40 rounded-lg px-4 py-3 flex items-center gap-3">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
            <circle cx="12" cy="12" r="10"/>
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
          </svg>
          <div>
            <p className="text-red-400 text-xs font-semibold">AI Content - Upload Blocked</p>
            <p className="text-red-300/70 text-xs">
              {result.aiDetection.detectedTools.length > 0
                ? `Source: ${result.aiDetection.detectedTools.join(', ')}`
                : result.aiDetection.note}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

function StatusPill({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className={`rounded-lg px-2 py-1.5 text-center ${
      ok ? 'bg-green-900/30 text-green-400' : 'bg-phenom-surface text-phenom-gray/50'
    }`}>
      <span className="mr-1">{ok ? '✓' : '—'}</span>
      {label}
    </div>
  )
}

export default App
